// client/src/components/pages/LexiStar/Declarations/useScopeReference.js
//
// Reference lists for a declaration's scope picker and for naming scope ids in
// the list: routes and lanes (M2 routes module), sites (the locations API),
// trucks and their classes (M1 asset register). Each list loads once per page;
// a failed list is simply empty (the picker then says so).

import { useEffect, useMemo, useState } from 'react';
import {
  listRoutes,
  listLanes,
  listSites,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import { listAssets } from '../../../../services/lexistarAssets';

const settle = (p) => p.then((r) => r).catch(() => null);

export default function useScopeReference() {
  const [data, setData] = useState({ routes: [], lanes: [], sites: [], assets: [], loaded: false });

  useEffect(() => {
    let alive = true;
    Promise.all([
      settle(listRoutes()),
      settle(listLanes()),
      settle(listSites()),
      settle(listAssets({ limit: 500 })),
    ]).then(([r, l, s, a]) => {
      if (!alive) return;
      setData({
        routes: pickList(r, 'routes').filter((x) => x.status !== 'retired'),
        lanes: pickList(l, 'lanes'),
        sites: pickList(s, 'locations'),
        assets: pickList(a, 'assets').filter((x) => x.asset_type !== 'trailer'),
        loaded: true,
      });
    });
    return () => {
      alive = false;
    };
  }, []);

  return useMemo(() => {
    const options = {
      route: data.routes.map((r) => ({
        value: String(r.id),
        label: r.code ? `${r.code} · ${r.name}` : r.name,
      })),
      lane: data.lanes.map((l) => ({
        value: String(l.id),
        label:
          l.name || `${l.from_name || l.from_location_name} → ${l.to_name || l.to_location_name}`,
      })),
      site: data.sites.map((s) => ({
        value: String(s.location_id ?? s.id),
        label: s.location_name || s.name,
      })),
      fleet: data.assets
        .map((a) => ({
          value: a.registration_normalised ? `reg:${a.registration_normalised}` : null,
          label: a.fleet_no ? `${a.registration} (${a.fleet_no})` : a.registration,
        }))
        .filter((o) => o.value),
      vehicle_class: [
        ...new Set(data.assets.map((a) => a.vehicle_class || a.asset_type).filter(Boolean)),
      ].map((c) => ({
        value: c,
        label: c.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase()),
      })),
    };
    const names = {};
    Object.values(options).forEach((list) => list.forEach((o) => (names[o.value] = o.label)));
    const routeVersions = Object.fromEntries(
      data.routes.map((r) => [String(r.id), r.current_version])
    );
    return { options, names, routeVersions, loaded: data.loaded };
  }, [data]);
}
